import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import BaseWidget from './BaseWidget';
import { buildRevenueSeries, hasDashboardSourceError } from '../../lib/dashboardMetrics';

// Read a CSS custom property value at render time so Recharts SVG attributes
// (which can't use CSS variables directly) track the active theme.
function readCssVar(varName) {
  if (typeof window === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
}
/**
 * RevenueChartWidget Component
 *
 * Displays revenue data as a line or bar chart
 */
const RevenueChartWidget = ({ id, config = {}, onRemove, onConfigure, isEditing, theme = 'light', metrics }) => {
  const {
    chartType = 'line',
    timeframe = '30d',
    showComparison = false,
    color,
    data = []
  } = config;

  // Re-read CSS variable values whenever theme changes so Recharts SVG
  // stroke/fill attributes (which can't use CSS variables directly) stay current.
  const chartColors = useMemo(() => {
    void theme;
    return {
      grid: readCssVar('--surface-border'),
      axis: readCssVar('--ink-muted'),
      comparison: readCssVar('--ink-muted'),
    };
  }, [theme]);

  const seriesColor = useMemo(() => {
    void theme;
    return color || readCssVar('--accent-ink');
  }, [color, theme]);

  // BL-194 Gate 1: the chart reads real collected/invoiced revenue from
  // dashboard_revenue_trend. The random sample series this widget used to
  // generate is gone — a dashboard that invents numbers is worse than an empty
  // one. An explicit config.data still wins for a hand-fed tile.
  const chartData = useMemo(() => {
    if (data && data.length > 0) {
      return data;
    }

    const series = buildRevenueSeries(metrics?.revenueTrend || [], timeframe);
    return series.map((point) => ({
      date: point.label,
      revenue: point.revenue,
      comparison: showComparison ? point.invoiced : undefined
    }));
  }, [data, timeframe, showComparison, metrics]);

  const hasData = chartData.some((point) => point.revenue || point.comparison);
  const sourceUnavailable = !(data && data.length > 0)
    && hasDashboardSourceError(metrics, 'dashboard_revenue_trend');

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[var(--surface-raised)] border border-[var(--surface-border)] rounded-lg shadow-lg p-3">
          <p className="text-sm font-medium text-[var(--ink)] mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: ${entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <BaseWidget
      id={id}
      title="Revenue Chart"
      onRemove={onRemove}
      onConfigure={onConfigure}
      isRemovable={isEditing}
      isConfigurable={isEditing}
      theme={theme}
      icon={
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
        </svg>
      }
      headerActions={
        isEditing ? (
          <div className="widget-drag-handle px-2 py-1 cursor-move">
            <svg className="w-4 h-4 text-[var(--ink-muted)]" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </div>
        ) : null
      }
    >
      <div className="h-full flex flex-col">
        {/* Chart Stats */}
        <div className="flex items-center justify-between mb-4 pb-2 border-b border-[var(--surface-border)]">
          <div>
            <p className="text-xs text-[var(--ink-muted)]">Total Revenue</p>
            <p className="text-lg font-bold text-[var(--ink)]">
              {sourceUnavailable ? '—' : `$${chartData.reduce((sum, item) => sum + item.revenue, 0).toLocaleString()}`}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-[var(--ink-muted)]">Average</p>
            <p className="text-lg font-bold text-[var(--ink)]">
              {sourceUnavailable ? '—' : `$${chartData.length ? Math.floor(chartData.reduce((sum, item) => sum + item.revenue, 0) / chartData.length).toLocaleString() : '0'}`}
            </p>
          </div>
        </div>

        {/* Chart */}
        <div className="flex-1" style={{ minHeight: '200px' }}>
          {!hasData || sourceUnavailable ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <p className="text-sm text-[var(--ink-muted)]" role="status">
                {sourceUnavailable ? 'Revenue data temporarily unavailable' : 'No revenue recorded in this period'}
              </p>
            </div>
          ) : (
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'line' ? (
              <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  stroke={chartColors.axis}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  stroke={chartColors.axis}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip content={<CustomTooltip />} />
                {showComparison && <Legend />}
                <Line
                  type="monotone"
                  dataKey="revenue"
                  name="Revenue"
                  stroke={seriesColor}
                  strokeWidth={2}
                  dot={{ fill: seriesColor, r: 3 }}
                  activeDot={{ r: 5 }}
                />
                {showComparison && (
                  <Line
                    type="monotone"
                    dataKey="comparison"
                    name="Previous Period"
                    stroke={chartColors.comparison}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: chartColors.comparison, r: 3 }}
                  />
                )}
              </LineChart>
            ) : (
              <BarChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  stroke={chartColors.axis}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  stroke={chartColors.axis}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip content={<CustomTooltip />} />
                {showComparison && <Legend />}
                <Bar dataKey="revenue" name="Revenue" fill={seriesColor} radius={[4, 4, 0, 0]} />
                {showComparison && (
                  <Bar dataKey="comparison" name="Previous Period" fill={chartColors.comparison} radius={[4, 4, 0, 0]} />
                )}
              </BarChart>
            )}
          </ResponsiveContainer>
          )}
        </div>
      </div>
    </BaseWidget>
  );
};

export default RevenueChartWidget;
